<script lang="ts">
  import { onMount } from 'svelte';
  import { _ } from 'svelte-i18n';
  import {
    Flame,
    TrendingUp,
    BookOpen,
    Award,
    ChevronRight,
    Image,
    Zap,
    Trophy,
    Calendar,
    Bell,
    Play

  } from 'lucide-svelte';
  import Card from './ui/card.svelte';
  import Button from './ui/button.svelte';
  import { navigationActions } from '../stores/navigation';
  import { flashcards, totalCards, learnedCount, selectedTargetLanguage } from '../stores/flashcards';
  import {
    userProgress,
    todayStudyMinutes,
    leagueTitle,
    progressActions,
  } from '../stores/progress';
  import { xpState, xpActions } from '../stores/xp';
  import { XPService, type LeagueProgress as LeagueProgressType, type League } from '../lib/xp-service';
  import { supabase } from '../lib/supabase';
  import GamificationInfoDialog from './dialogs/GamificationInfoDialog.svelte';

  type Notification = {
    id: string;
    text: string;
    segment: string;
    type: string;
    status: string;
    created_at: string;
  };

  const weeklyStreak = $derived($userProgress.weekly_streak ?? 0);
  const todayMinutes = $todayStudyMinutes ?? 0;
  const leagueLabel = $leagueTitle;

  // XP state
  const totalXP = $derived($xpState.total_xp);
  const currentLevel = $derived($xpState.current_level);
  const xpProgress = $derived($xpState.level_progress_percentage);
  const xpToNextLevel = $derived($xpState.xp_to_next_level);

  let showGamificationInfo = $state(false);
  let todayXP = $state(0);
  let weeklyXP = $state(0);
  let weeklyGoalXP = $state(100);
  let leagueProgress = $state<LeagueProgressType | null>(null);
  let allLeagues = $state<League[]>([]);
  let isLoadingLeagueCard = $state(true);
  let leagueCardError = $state<string | null>(null);
  let notifications = $state<Notification[]>([]);
  let isLoadingNotifications = $state(true);

  const notificationDateFormatter = new Intl.DateTimeFormat(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  function formatNotificationDate(dateString: string) {
    if (!dateString) return '';
    return notificationDateFormatter.format(new Date(dateString));
  }

  async function loadXPData() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      leagueCardError = null;
      isLoadingLeagueCard = true;

      const [progress, leagues] = await Promise.all([
        XPService.getLeagueProgress(user.id),
        XPService.getAllLeagues(),
      ]);

      leagueProgress = progress;
      allLeagues = leagues;

      if (progress) {
        weeklyXP = progress.weekly_xp;
        weeklyGoalXP = progress.weekly_goal;
      }

      // Get league progress for weekly XP and goal
      // Get today's XP from log
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const { data: xpLog, error } = await supabase
        .from('user_xp_log')
        .select('xp')
        .eq('user_id', user.id)
        .gte('date', today.toISOString());

      if (!error && xpLog) {
        // @ts-ignore - Supabase types mismatch
        todayXP = xpLog.reduce((sum, log) => sum + log.xp, 0);
      }
    } catch (err) {
      console.error('Failed to load XP data:', err);
      leagueCardError = $_('dashboard.league_error');
    } finally {
      isLoadingLeagueCard = false;
    }
  }

  async function loadNotifications() {
    isLoadingNotifications = true;
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('status', 'published')
        .order('created_at', { ascending: false })
        .limit(3);

      if (error) throw error;
      notifications = data || [];
    } catch (err) {
      console.error('Failed to load notifications:', err);
    } finally {
      isLoadingNotifications = false;
    }
  }

  onMount(async () => {
    await progressActions.loadProgress();
    await xpActions.loadRewards();
    await xpActions.loadXPState();
    await loadXPData();
    await loadNotifications();
  });

  const daysUntilWeekEnd = $derived(XPService.getDaysUntilWeekEnd());
  const isGracePeriod = $derived(XPService.isInGracePeriod());

</script>

<div class="min-h-screen pb-24">
  <div class="mx-auto max-w-md px-4 py-8 space-y-8">
    <!-- Current League Card -->
    <Card class="widget-card tone-header p-6 min-h-[280px]">
    {#if isLoadingLeagueCard}
      <div class="flex h-full flex-col justify-between gap-6 py-2">
        <div class="flex items-start justify-between gap-4">
          <div class="space-y-3">
                <div class="h-3 w-28 rounded-full bg-foreground/20 animate-pulse"></div>
                <div class="h-8 w-40 rounded-full bg-foreground/15 animate-pulse"></div>
                <div class="h-3 w-32 rounded-full bg-foreground/20 animate-pulse"></div>
              </div>
              <div class="h-20 w-32 rounded-2xl bg-foreground/15 animate-pulse"></div>
            </div>

            <div class="space-y-4">
              <div class="h-16 rounded-2xl bg-foreground/10 animate-pulse"></div>
              <div class="h-4 w-full rounded-full bg-foreground/10 animate-pulse"></div>
              <div class="h-4 w-1/3 rounded-full bg-foreground/10 animate-pulse"></div>
            </div>
          </div>
        {:else if leagueCardError}
          <div class="p-4 rounded-2xl border border-destructive/30 bg-destructive/10 text-destructive">
            {leagueCardError}
          </div>
        {:else if leagueProgress}
          <button
            type="button"
            onclick={() => showGamificationInfo = true}
            class="block w-full text-left focus:outline-none"
          >
            <div class="mb-5 flex items-start justify-between gap-4">
              <div class="flex items-center gap-3">
                <span class="text-5xl">{leagueProgress.league_emoji}</span>
                <div>
                  <p class="text-xs uppercase tracking-[0.28em] text-foreground/70">{$_('dashboard.current_league')}</p>
                  <h2 class="text-2xl font-bold text-foreground">{leagueProgress.league_name}</h2>
                  <p class="text-sm text-foreground/70">
                    {$_('dashboard.league_label')} {leagueProgress.league_order} / {allLeagues.length}
                  </p>
                </div>
              </div>
              <div class="rounded-2xl widget-muted px-4 py-3 text-right">
                <p class="text-xs uppercase tracking-[0.2em] text-foreground/60">{$_('dashboard.today')}</p>
                <p class="text-2xl font-semibold text-foreground">{todayXP} XP</p>
              </div>
            </div>

            {#if leagueProgress.comeback_boost_active}
              <div class="mb-4 rounded-2xl border border-amber-200 bg-amber-100/80 p-3 text-amber-800">
                <div class="flex items-center gap-2 font-semibold">
                  <Zap class="h-5 w-5" />
                  {$_('dashboard.comeback_boost')}
                </div>
              </div>
            {/if}

            <div class="mb-6 rounded-2xl border border-foreground/20 bg-foreground/5 p-4 shadow-soft-sm">
              <div class="mb-2 flex items-center justify-between text-xs uppercase tracking-[0.24em] text-foreground/60">
                <span>{$_('dashboard.weekly_goal')}</span>
                <span class="text-foreground/80">{leagueProgress.weekly_xp} / {leagueProgress.weekly_goal} XP</span>
              </div>
              <div class="flex h-4 w-full items-center gap-2 rounded-full px-1">
                <div
                  class="h-1.5 rounded-full transition-all duration-500"
                  style={`width: ${Math.min(100, leagueProgress.progress_percentage)}%; background: linear-gradient(to right, hsl(var(--primary)), hsl(var(--secondary)), hsl(var(--accent)));`}
                ></div>
                <span class="text-xs font-semibold text-foreground/70">
                  {Math.min(100, leagueProgress.progress_percentage)}%
                </span>
              </div>
            </div>

            <div class="grid grid-cols-2 gap-4">
              <div class="flex items-center gap-2 text-sm text-foreground/80">
                <TrendingUp class="h-5 w-5 text-foreground/60" />
                <span>
                  {$_('dashboard.series_label')}: <strong>{leagueProgress.current_streak} {$_('dashboard.series_unit')}</strong>
                  {#if leagueProgress.min_streak_required > 0}
                    <div class="text-xs">({$_('dashboard.min_label')} {leagueProgress.min_streak_required})</div>
                  {/if}
                </span>
              </div>

              <div class="flex items-center gap-2 text-sm text-foreground/70">
                <Calendar class="h-5 w-5 text-foreground/60" />
                <span>
                  {#if daysUntilWeekEnd === 0}
                    {$_('dashboard.week_ends_today')}
                  {:else}
                    Zbývá {daysUntilWeekEnd} {daysUntilWeekEnd === 1 ? 'den' : daysUntilWeekEnd < 5 ? 'dny' : 'dnů'}
                  {/if}
                  {#if isGracePeriod}
                    <span class="text-amber-600">({$_('dashboard.grace_period')})</span>
                  {/if}
                </span>
              </div>
            </div>
          </button>
        {:else}
          <div class="p-4 rounded-2xl border border-foreground/20 bg-foreground/5">
            {$_('dashboard.no_league_data')}
          </div>
        {/if}
    </Card>

    <!-- Notifications Section -->
    <section class="space-y-3">
      <h2 class="section-title flex items-center gap-2">
        <Bell class="h-5 w-5" />
        {$_('dashboard.notifications')}
      </h2>

      {#if isLoadingNotifications}
        <div class="space-y-3">
          {#each Array(3) as _}
            <Card class="widget-card tone-header p-4">
              <div class="flex items-start gap-3">
                <div class="rounded-full bg-primary/10 p-2 flex-shrink-0">
                  <div class="h-5 w-5 bg-primary/30 rounded" aria-hidden />
                </div>
                <div class="flex-1 space-y-2">
                  <div class="h-3 w-24 rounded-full bg-foreground/10 animate-pulse" aria-hidden></div>
                  <div class="h-4 w-full rounded-full bg-foreground/10 animate-pulse" aria-hidden></div>
                  <div class="h-4 w-2/3 rounded-full bg-foreground/10 animate-pulse" aria-hidden></div>
                </div>
              </div>
            </Card>
          {/each}
        </div>
      {:else if notifications.length > 0}
        <div class="space-y-3">
          {#each notifications as notification}
            <Card class="widget-card tone-header p-4">
              <div class="flex items-start gap-3">
                <div class="rounded-full bg-primary/10 p-2 flex-shrink-0">
                  <Bell class="h-5 w-5 text-primary" />
                </div>
                <div class="flex-1 space-y-1">
                  <p class="text-xs text-foreground/60">
                    {$_('dashboard.notifications_date_prefix')} {formatNotificationDate(notification.created_at)}
                  </p>
                  <p class="text-sm text-foreground leading-relaxed">{notification.text}</p>
                </div>
              </div>
            </Card>
          {/each}
        </div>
      {:else}
        <Card class="widget-card tone-header p-4">
          <div class="flex items-center gap-2 text-sm text-foreground/70">
            <Bell class="h-4 w-4 text-foreground/50" />
            <span>{$_('dashboard.notifications_empty')}</span>
          </div>
        </Card>
      {/if}
    </section>

  </div>
</div>

{#if showGamificationInfo}
  <GamificationInfoDialog onClose={() => (showGamificationInfo = false)} />
{/if}
